
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { v4 as uuid } from "uuid";

import InboxWindowHead from "@/components/inbox/InboxWindowHead.vue";
import InboxWindowMessages from "@/components/inbox/InboxWindowMessages.vue";
import InboxWindowTextarea from "@/components/inbox/InboxWindowTextarea.vue";
import InboxWindowEmpty from "@/components/inbox/InboxWindowEmpty.vue";
import { Conversation, Message, User, User_Conversation } from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import CustomScroll from "@/components/scroll/CustomScroll.vue";
import ConversationParticipants from "@/entities/ConversationParticipants";
import getConversationParticipants from "@/helpers/inboxHelpers/getConversationParticipants";
import ConversationUserData from "@/entities/ConversationUserData";
import splitFileNameExt from "@/utils/splitFileNameExt";

@Component({
  components: {
    CustomScroll,
    InboxWindowHead,
    InboxWindowMessages,
    InboxWindowTextarea,
    InboxWindowEmpty,
  },
})
export default class InboxWindow extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, default: undefined }) readonly uploadingAttachmentId!: string;
  @Prop({ type: String, default: undefined }) readonly presetMessage!: string;
  @Prop({ type: Object, default: undefined }) readonly conversation!: Conversation;
  @Prop({type: String, default: undefined}) readonly selectedConversationId?: string;
  @Prop({ type: String, default: undefined }) readonly predefinedMessage?: string;
  @Prop({ type: Boolean, default: true }) readonly canLeave?: boolean;

  input: string = "";
  showConversation: boolean = false;
  messages: Partial<Message>[] = [];

  get conversationToShowExists(): boolean {
    return this.conversation && this.showConversation;
  }

  get participants(): ConversationParticipants | undefined {
    return getConversationParticipants(this.conversation.users, this.currentUser.id);
  }

  get isGroupConversation(): boolean {
    const participants = this.conversation.users
      .filter((conversationUser: User_Conversation) => conversationUser.user.id !== this.currentUser.id)
    return participants.length > 1;
  }

  get onlyParticipant(): ConversationUserData | undefined{
    if (!this.participants?.all.length) {
      return undefined;
    }
    return this.participants!.all[0];
  }

  get noParticipants(): boolean {
    if (!this.participants) {
      return true;
    }
    return Boolean(this.participants.all.length == 1 && this.onlyParticipant?.deleted);
  }

  @Watch('conversation', {immediate: true, deep: true})
  onConversationChange(): void {
    this.messages = this.conversation?.messages  || [];

    if (this.conversation?.id === this.selectedConversationId) {
      this.showConversation = true;
    }
    this.showConversation = true;
  }

  newMessageAdded(newMessage: {content: string, attachment: Record<string, any>}) {
    const id = uuid();
    const attachmentName = newMessage.attachment?.name;
    const content = newMessage.content;
    const {
      avatar,
      given_name,
      family_name,
    } = this.currentUser;

    const sender = {
      id: this.currentUser.id,
      avatar,
      given_name,
      family_name,
    }

    if (!attachmentName) {
      const message: any = {
        id,
        content,
        sender,
      };

      this.messages = [
        message,
        ...this.messages,
      ];
      this.$emit('newMessage', newMessage);
      return;
    }

    const { ext, name } = splitFileNameExt(attachmentName);
    const fileName = `${name}-${id}.${ext}`;

    const message: any = {
      id,
      content,
      attachment: fileName,
      sender,
    };

    this.messages = [
      message,
      ...this.messages,
    ];
    this.$emit('newMessage', { id, ...newMessage });
  }

  handleConversationDeleted(id: string) {
    this.showConversation = false;
    this.$emit("conversationDeleted", id);
    this.$router.replace({name: RouteNames.INBOX, params:{}});
  }
}
